import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import loaderGif from "../assets/Img/loading1.gif";
import DataTable from "react-data-table-component";
import "../assets/CSS/Dashboard.css";

export default function BOMVerification() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedLot, setSelectedLot] = useState("");
  const [lotIds, setLotIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [loadingLots, setLoadingLots] = useState(false);
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user")) || {};
  const loggedInUser = user.Name || "Unknown";

  const toggleSidebar = () => setIsSidebarVisible(v => !v);

  const fetchLotIds = async () => {
    setLoadingLots(true);
    try {
      const resp = await axiosInstance.get("/api/Tall-Bom/lotids");
      setLotIds(resp.data.lotIds || []);
    } catch (err) {
      console.error("Error loading lot IDs:", err);
    } finally {
      setLoadingLots(false);
    }
  };

  const fetchRecords = async () => {
    setLoadingRecords(true);
    try {
      const resp = await axiosInstance.get("/api/Tally-Bom");
      setRecords(resp.data.records || []);
    } catch (err) {
      console.error("Error loading BOM records:", err);
    } finally {
      setLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchLotIds();
    fetchRecords();
  }, []);

  const handleFileChange = e => setSelectedFile(e.target.files[0] || null);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', selectedFile);
    fd.append('UploadedBy', loggedInUser);
    try {
      const resp = await axiosInstance.post(
        '/api/tally-Bom/import',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const count = resp.data.insertedRows || 0;
      showAlert(`${count} record(s) imported.`, 'success');
      fileInputRef.current.value = null;
      setSelectedFile(null);
      await fetchLotIds();
      await fetchRecords();
    } catch (err) {
      console.error('Import error:', err);
      if (err.response?.status === 409) {
        showAlert('This file has already been uploaded.', 'warning');
      } else {
        showAlert('Import failed. Please try again.', 'danger');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleLotSelect = e => setSelectedLot(e.target.value);

  const handleDelete = async () => {
    if (!selectedLot) return;
    if (!window.confirm(`Delete records for ${selectedLot}?`)) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/api/tally-bom/${encodeURIComponent(selectedLot)}`);
      showAlert('Deleted successfully.', 'success');
      setSelectedLot("");
      await fetchLotIds();
      await fetchRecords();
    } catch (err) {
      console.error('Delete error:', err);
      showAlert('Delete failed.', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const showAlert = (msg, type) => {
    const ph = document.getElementById('alertPlaceholder');
    ph.innerHTML =
      `<div class="alert alert-${type} alert-dismissible fade show" role="alert">
         <strong>${type === 'success' ? 'Success!' : 'Error!'}</strong> ${msg}
         <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
       </div>`;
    setTimeout(() => ph.innerHTML = '', 3000);
  };

  const columns = [
    { name: 'Voucher No', selector: r => r.Voucher_Number, sortable: true },
    { name: 'Item Code', selector: r => r.Item_Code, sortable: true },
    { name: 'Item Name', selector: r => r.Item_Name, sortable: true },
    { name: 'JO No.', selector: r => r.Jo_No, sortable: true },
    { name: 'Godown', selector: r => r.Godown_Name, sortable: true },
    { name: 'Uploaded By', selector: r => r.UploadedBy, sortable: true },
     { name: 'Uploaded At', selector: r => r.UploadedAt, sortable: true },
  
  ];

  return (
    <div className="d-flex dashboard">
      <style>{`
        .overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.5); display: flex;
          align-items: center; justify-content: center; z-index: 9999;
        }
        .loader { width: 80px; height: 80px; }
      `}</style>

      {uploading && selectedFile && (
        <div className="overlay">
          <img src={loaderGif} alt="Uploading..." className="loader" />
        </div>
      )}

      <div className={isSidebarVisible ? 'sidebar-container' : 'sidebar-hidden'}>
        <Sidebar />
      </div>
      <div className="flex-grow-1">
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />
        <main className="main-container p-4" style={{ height: 'fit-content' }}>
          <div id="alertPlaceholder" />
          <h3 className="mb-4">Tally BOM Upload</h3>

          <div className="d-flex mb-3">
            <div className="me-5">
              <label className="form-label">Import BOM Excel:</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="form-control"
                ref={fileInputRef}
              />
              <button
                className="btn btn-primary mt-2"
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
              >
                {uploading ? 'Importing...' : 'Import BOM'}
              </button>
            </div>
            <div>
              <label className="form-label">Select Lot to Delete:</label>
              <select
                className="form-select"
                value={selectedLot}
                onChange={handleLotSelect}
                disabled={loadingLots}
                style={{ width: 200 }}
              >
                <option value="">-- Select Lot ID --</option>
                {lotIds.map(lot => (
                  <option key={lot} value={lot}>{lot}</option>
                ))}
              </select>
              <button
                className="btn btn-danger mt-2"
                onClick={handleDelete}
                disabled={deleting || !selectedLot}
              >
                {deleting ? 'Deleting...' : 'Delete BOM'}
              </button>
            </div>
          </div>

          {loadingRecords ? (
            <div className="text-center">
              <span className="spinner-border" /> Loading...
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={records}
              pagination
              highlightOnHover
              striped
            />
          )}
        </main>
      </div>
    </div>
  );
}
